import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import React, { useState, useMemo, useEffect } from "react"
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaPlus,
  FaSave,
} from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Input,
  Form,
  Label,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"

const PromotionCode = () => {
  document.title = "Promotion Codes | Milano Jewelry"
  const { checkAccess } = usePermissionData()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [searchText, setSearchText] = useState("")
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  useEffect(() => {
    getData()
  }, [])

  async function getData() {
    const APIdata = await fetchData(setLoading, "promotion/code")
    if (APIdata?.success) setData(APIdata?.data)
  }

  const toggleModal = () => setModalOpen(!modalOpen)
  const handleEdit = item => {
    setEditingItem(item)
    toggleModal()
  }

  const handleSave = async () => {
    if (!editingItem?.promotionCode?.trim()) {
      Swal.fire({ title: "Promotion Code is required", icon: "warning" })
      return
    }
    if (
      editingItem?.markup === undefined ||
      editingItem?.markup === "" ||
      editingItem?.markup === null
    ) {
      Swal.fire({ title: "Markup is required", icon: "warning" })
      return
    }
    if (Number(editingItem?.markup) < 0) {
      Swal.fire({ title: "Markup (%) must be 0 or greater", icon: "warning" })
      return
    }
    if (editingItem?.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "promotion/code",
        editingItem?.id,
        editingItem
      )
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Promotion Code Updated Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => getData(), 1500)
      } else {
        Swal.fire({
          title: "Error Updating Promotion Code",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(
        setLoading,
        "promotion/code",
        editingItem
      )
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Promotion Code Added Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => getData(), 1500)
      } else {
        Swal.fire({
          title: "Error Adding Promotion Code",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = async item => {
    const vendorMarkupsRes = await fetchData(
      setLoading,
      "vendor/markup/all/vendors"
    )
    if (vendorMarkupsRes?.success) {
      const usedByVendor = vendorMarkupsRes.data.find(
        m => m.promotionCode === item.promotionCode
      )
      if (usedByVendor) {
        Swal.fire({
          title: "Cannot Delete",
          text: `This promotion code is assigned to vendor "${
            usedByVendor.vendor?.company || usedByVendor.vendor?.email || usedByVendor.vendor?.name
          }" and cannot be deleted.`,
          icon: "error",
        })
        return
      }
    }

    const confirm = await Swal.fire({
      title: "Are you Sure?",
      text: `You want to Delete this ${item?.promotionCode} Promotion Code`,
      icon: "warning",
      showCancelButton: true,
    })
    if (!confirm.isConfirmed) return
    const APIdata = await FetchDeleteData(
      setLoading,
      "promotion/code",
      item?.id
    )
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Promotion Code Deleted Successfully",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setTimeout(() => getData(), 1500)
    } else {
      Swal.fire({
        title: "Error deleting Promotion Code",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const filtered = useMemo(
    () =>
      data.filter(d =>
        d?.promotionCode?.toLowerCase().includes(searchText.toLowerCase())
      ),
    [data, searchText]
  )
  const totalPages = Math.ceil(filtered.length / perPage)
  const paginated = filtered.slice((page - 1) * perPage, page * perPage)

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Settings" breadcrumbItem="Promotion Code" />
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-3">
              <select
                className="form-select"
                style={{ width: "100px" }}
                value={perPage}
                onChange={e => {
                  setPerPage(Number(e.target.value))
                  setPage(1)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={e => {
                  setSearchText(e.target.value)
                  setPage(1)
                }}
              />
            </div>
            {checkAccess(15, "add") && (
              <Button
                className="btn-blue"
                onClick={() => {
                  toggleModal()
                  setEditingItem({})
                }}
              >
                <FaPlus className="me-1" /> Add
              </Button>
            )}
          </div>
          <Table bordered responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Promotion Code</th>
                <th>Markup (%)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length > 0 ? (
                paginated.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(page - 1) * perPage + index + 1}</td>
                    <td>{item.promotionCode}</td>
                    <td>{item.markup ? `${item.markup} %` : "-"}</td>
                    <td>
                      {checkAccess(15, "edit") && (
                        <Button
                          color="success"
                          className="me-2"
                          onClick={() => handleEdit(item)}
                        >
                          <FaEdit />
                        </Button>
                      )}
                      {checkAccess(15, "delete") && (
                        <Button
                          color="danger"
                          onClick={() => handleDelete(item)}
                        >
                          <MdDelete />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center">
                    No Promotion Codes found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between">
            <span className="align-self-center">
              Page {page} of {totalPages || 1}
            </span>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <Button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="btn-blue"
              >
                <FaChevronLeft />
              </Button>
              {(() => {
                const nums = []
                const max = 5
                let start = Math.max(1, page - Math.floor(max / 2))
                let end = start + max - 1
                if (end > totalPages) {
                  end = totalPages
                  start = Math.max(1, end - max + 1)
                }
                for (let i = start; i <= end; i++)
                  nums.push(
                    <Button
                      key={i}
                      color={page === i ? "primary" : "light"}
                      onClick={() => setPage(i)}
                    >
                      {i}
                    </Button>
                  )
                return nums
              })()}
              <Button
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage(p => p + 1)}
                className="btn-blue"
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
      <Modal isOpen={modalOpen} centered>
        <ModalHeader toggle={toggleModal}>
          {editingItem?.id ? "Edit Promotion Code" : "Add Promotion Code"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Label>Promotion Code</Label>
            <Input
              type="text"
              value={editingItem?.promotionCode || ""}
              onChange={e =>
                setEditingItem(prev => ({
                  ...prev,
                  promotionCode: e.target.value.replace(/^\s+/, ""),
                }))
              }
            />
            <Label className="mt-3">Markup (%)</Label>
            <Input
              type="number"
              min="0"
              value={editingItem?.markup ?? ""}
              onChange={e =>
                setEditingItem(prev => ({
                  ...prev,
                  markup: e.target.value,
                }))
              }
            />
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSave}>
            <FaSave /> Save
          </Button>{" "}
          <Button color="secondary" onClick={toggleModal}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default PromotionCode
